import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {FolderWithPermissions} from 'constants/FolderConstants';
import {Batch, StorableSummary, StorableSummaryExtras} from 'constants/SampleConstants';
import {SequenceAnalysisSummary} from 'constants/SequenceAnalysisConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ProteinAlignmentSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {EntrySummary, ProtocolSummary} from 'notebook/ELNConstants';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import {Request} from 'requests/RequestConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {AsyncSearchResponse, GLOBAL_SEARCH_URL, SearchParams, SearchResults} from 'search/SearchConstants';
import {postSearchRequest} from 'search/SearchDAO';
import {StageEntrySummary, WorkflowSummary} from 'workflows/WorkflowConstants';

import {AnalysisSearchAttributesGraphQL} from '../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {JellyfishListingGraphQL} from '../../graphql-fragments/app-core/archetype/__generated/JellyfishListingGraphQL';
import {BenchlingAppGraphQL} from '../../graphql-fragments/benchling-apps/__generated/BenchlingAppGraphQL';
import {DatasetListingGraphQL} from '../../graphql-fragments/data-frames/__generated/DatasetListingGraphQL';
import {FileListingGraphQL} from '../../graphql-fragments/files/__generated/FileListingGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';
import {ConnectionListingGraphQL} from '../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {BulkAssemblyListingGraphQL} from '../../graphql-fragments/molbio/__generated/BulkAssemblyListingGraphQL';
import {ProteinAlignmentSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/ProteinAlignmentSummaryGraphQL';
import {SequenceAnalysisSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/SequenceAnalysisSummaryGraphQL';
import {ProjectSummaryGraphQL} from '../../graphql-fragments/nav/sources/__generated/ProjectSummaryGraphQL';
import {CollectibleListingGraphQL} from '../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';
import {TemplateCollectionListingGraphQL} from '../../graphql-fragments/notebook/templates/__generated/TemplateCollectionListingGraphQL';
import {ProcessListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessListingGraphQL';
import {ProcessOutputListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessOutputListingGraphQL';
import {ProcessTaskListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {RequestV2SubmissionListingGraphQL} from '../../graphql-fragments/processes/requests-v2/__generated/RequestV2SubmissionListingGraphQL';
import {TestDefinitionListingGraphQL} from '../../graphql-fragments/sample_tests/__generated/TestDefinitionListingGraphQL';
import {TestOrderListingGraphQL} from '../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import {StudyListingGraphQL} from '../../graphql-fragments/studies/__generated/StudyListingGraphQL';
import {StageEntrySummaryGraphQL} from '../../graphql-fragments/workflows/__generated/StageEntrySummaryGraphQL';
import {WorklistSummaryGraphQL} from '../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {WorksheetListingGraphQL} from '../../graphql-fragments/worksheets/__generated/WorksheetListingGraphQL';

export interface GlobalSearchResponse extends SearchResults {
  analysesById: Record<string, AnalysisSearchAttributesGraphQL>;
  analyticsDashboardsById: Record<string, AnalyticsDashboardSearchAttributesGraphQL>;
  batchesById: Record<string, Batch>;
  benchlingAppsById: Record<string, BenchlingAppGraphQL>;
  bulkAssembliesById: Record<string, BulkAssemblyListingGraphQL>;
  collectiblesById: Record<string, CollectibleListingGraphQL>;
  connectionsById: Record<string, ConnectionListingGraphQL>;
  datasetsById: Record<string, DatasetListingGraphQL>;
  entrySummariesById: Record<string, EntrySummary>;
  filesById: Record<string, FileListingGraphQL>;
  fileSummariesById: Record<string, BioentitySummary>;
  foldersById: Record<string, FolderWithPermissions>;
  jellyfishesById: Record<string, JellyfishListingGraphQL>;
  permissionsByModelId: Record<string, Permissions>;
  processesById: Record<string, ProcessListingGraphQL>;
  processOutputsById: Record<string, ProcessOutputListingGraphQL>;
  processTasksById: Record<string, ProcessTaskListingGraphQL>;
  projectsById: Record<string, ProjectSummaryGraphQL>;
  proteinAlignmentSummariesById: Record<string, ProteinAlignmentSummary>; // Deprecated
  proteinAlignmentSummariesGraphQLById: Record<string, ProteinAlignmentSummaryGraphQL>;
  protocolSummariesById: Record<string, ProtocolSummary>;
  requestedTaskSchemaIdsByRequestId: Record<string, Array<string>>;
  requestsById: Record<string, Request>;
  requestV2SubmissionsById: Record<string, RequestV2SubmissionListingGraphQL>;
  runsById: Record<string, AssayRun>;
  sampleStatusCountsByRequestId: Record<string, RequestSampleStatusCounts>;
  sequenceAlignmentSummariesById: Record<string, SequenceAnalysisSummary>;
  sequenceAlignmentSummariesGraphQLById: Record<string, SequenceAnalysisSummaryGraphQL>;
  stageEntrySummariesById: Record<string, StageEntrySummary>;
  stageEntrySummariesGraphQLById: Record<string, StageEntrySummaryGraphQL>;
  storableSummariesById: Record<string, StorableSummary>;
  storableSummaryExtras: StorableSummaryExtras;
  studiesById: Record<string, StudyListingGraphQL>;
  templateCollectionsById: Record<string, TemplateCollectionListingGraphQL>;
  testDefinitionsById: Record<string, TestDefinitionListingGraphQL>;
  testOrdersById: Record<string, TestOrderListingGraphQL>;
  workflowSummariesById: Record<string, WorkflowSummary>;
  worklistSummariesById: Record<string, WorklistSummaryGraphQL>;
  worksheetsById: Record<string, WorksheetListingGraphQL>;
}

export function doGlobalSearch(
  searchParams: SearchParams,
  searchSource?: string
): Promise<GlobalSearchResponse | AsyncSearchResponse> {
  const url = `${GLOBAL_SEARCH_URL}?includeStorableExtras=false${
    BenchlingConfig.ENABLE_ASYNC_BLAST_SEARCH ? '&allowAsyncBlast=true' : ''
  }`;

  return postSearchRequest({url, data: {...searchParams, searchSource}});
}
